import { Component } from '@theme/component';

/**
 * <ms-tabs-component> — Tabs accesibles (WAI-ARIA Tabs pattern) para ms-bestsellers.
 *
 * Cambia el panel/colección activo SIN recargar la página: todos los paneles
 * (uno por tab/colección) ya vienen renderizados en el HTML; este componente
 * solo alterna `hidden` / `aria-selected` y mueve el foco con el teclado.
 *
 * Markup esperado (lo provee sections/ms-bestsellers.liquid):
 *   <ms-tabs-component>
 *     <div role="tablist">
 *       <button role="tab" id="..-tab-0" aria-controls="..-panel-0"
 *               aria-selected="true"  ref="tabs[]" on:click="/selectFromEvent"
 *               on:keydown="/onKeydown" data-index="0">...</button>
 *       ...
 *     </div>
 *     <a ref="viewAll[]" data-index="0" ...>Ver todos</a>  (opcional, 1 por tab)
 *     <div role="tabpanel" id="..-panel-0" ref="panels[]" data-index="0">...</div>
 *     ...
 *   </ms-tabs-component>
 *
 * Sin JS: el primer panel queda visible (los demás con [hidden]); cada tab es un
 * ancla nativa a su colección vía `data-fallback-href`, por lo que la navegación
 * y el product form siguen funcionando (degradación elegante).
 *
 * @extends {Component<{ tabs: HTMLButtonElement[], panels: HTMLElement[], viewAll: HTMLAnchorElement[] }>}
 */
class MsTabs extends Component {
  requiredRefs = ['tabs'];

  /** @type {number} Índice del tab activo. */
  #active = 0;

  connectedCallback() {
    super.connectedCallback();

    // Tomar el tab inicial marcado por Liquid (aria-selected="true"); si no, 0.
    const tabs = this.#tabs;
    const initial = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#active = initial >= 0 ? initial : 0;
    this.#select(this.#active, { moveFocus: false });
  }

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    const { tabs } = this.refs;
    return Array.isArray(tabs) ? tabs : tabs ? [tabs] : [];
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    const { panels } = this.refs;
    return Array.isArray(panels) ? panels : panels ? [panels] : [];
  }

  /** @returns {HTMLAnchorElement[]} */
  get #viewAllLinks() {
    const { viewAll } = this.refs;
    return Array.isArray(viewAll) ? viewAll : viewAll ? [viewAll] : [];
  }

  /**
   * Handler de click delegado (on:click="/selectFromEvent").
   * @param {Event} event
   */
  selectFromEvent(event) {
    const target = /** @type {HTMLElement} */ (event.target)?.closest('[role="tab"]');
    if (!target) return;

    const tabs = this.#tabs;
    const index = tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (index < 0) return;

    // Con JS activo el tab no navega: alterna el panel en su lugar.
    // Sin mover el foco a la fuerza (cosecha de Malara b7964fee): con el dedo
    // (Safari/iOS no enfoca botones al tocarlos) el focus() programático pintaba
    // el anillo de teclado y la fila lo recortaba en dos barras negras. Con el
    // mouse el navegador ya enfoca el botón solo; con Enter/Espacio el foco ya
    // está en la pestaña; las flechas siguen moviendo el foco (onKeydown).
    event.preventDefault();
    this.#select(index, { moveFocus: false });
  }

  /**
   * Navegación por teclado del tablist (roving tabindex).
   * Izquierda/Derecha mueven entre tabs; Home/End van al primero/último.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const tabs = this.#tabs;
    const count = tabs.length;
    if (count === 0) return;

    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (this.#active + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (this.#active - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(next, { moveFocus: true });
  }

  /**
   * Activa el tab/panel `index` y sincroniza ARIA + roving tabindex + "Ver todos".
   * @param {number} index
   * @param {Object} [options]
   * @param {boolean} [options.moveFocus] - Mover el foco al tab activado (solo teclado: flechas, Inicio, Fin).
   */
  #select(index, options = {}) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    if (index < 0 || index >= tabs.length) return;

    this.#active = index;

    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      // Roving tabindex: solo el tab activo es tabbable.
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });

    panels.forEach((panel, i) => {
      if (i === index) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    // Mostrar solo el "Ver todos" del tab activo (apunta a su colección).
    const links = this.#viewAllLinks;
    if (links.length > 0) {
      links.forEach((link, i) => {
        if (i === index) {
          link.removeAttribute('hidden');
        } else {
          link.setAttribute('hidden', '');
        }
      });
    }

    if (options.moveFocus) {
      tabs[index]?.focus();
    }
  }
}

if (!customElements.get('ms-tabs-component')) {
  customElements.define('ms-tabs-component', MsTabs);
}
